import { useContext, useEffect, useState } from 'react';
import { stripOrgPrefix } from '@/utils/org-path';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useOrgStore } from '@/stores/org-store';
import { AuthContext } from '@/context/auth-context';
import { OrgImage } from '@/components/org-image';
import { CopyButton } from '@/components/ui/copy-button';
import { useNotification } from '@/components/notifications';
import { RegionBadge } from '@/components/region-badge';
import { cn } from '@/lib/utils';
import { CreateOrganizationModal } from '@/pages/admin/settings/create-organization-modal';
import { Plus } from 'lucide-react';

export function OrgSwitcher() {
  const { isMobile, state } = useSidebar();
  const { currentOrg, orgs, setOrgs } = useOrgStore();
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);
  const notification = useNotification();
  const {
    isAdmin,
    orgSwitchBlockedMessage,
    clearOrgSwitchBlockedMessage,
    orgDeepLinkMessage,
    clearOrgDeepLinkMessage,
  } = useContext(AuthContext);

  useEffect(() => {
    if (orgSwitchBlockedMessage) {
      notification.warning(orgSwitchBlockedMessage);
      clearOrgSwitchBlockedMessage();
    }
  }, [orgSwitchBlockedMessage, notification, clearOrgSwitchBlockedMessage]);

  useEffect(() => {
    if (orgDeepLinkMessage) {
      if (orgDeepLinkMessage.severity === 'success') {
        notification.success(orgDeepLinkMessage.text);
      } else {
        notification.warning(orgDeepLinkMessage.text);
      }
      clearOrgDeepLinkMessage();
    }
  }, [orgDeepLinkMessage, notification, clearOrgDeepLinkMessage]);

  if (!currentOrg) {
    return null;
  }

  // With a single org and no way to make another, the menu would only list the
  // org you are already in, so the mark stays inert.
  const canSwitchOrgs = (orgs?.length ?? 0) > 1;
  const hasOrgMenu = canSwitchOrgs || isAdmin;

  const orgMark = (
    <div className='flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md'>
      <OrgImage
        imageUrl={currentOrg.branding?.logo_url}
        className='size-full object-cover'
      />
    </div>
  );

  if (!hasOrgMenu) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip={currentOrg.name}
            className='!p-0 justify-center'
          >
            {orgMark}
            <span className='sr-only'>{currentOrg.name}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <RegionBadge
            variant={state === 'collapsed' ? 'rail' : 'inline'}
            tooltipSide={state === 'collapsed' ? 'right' : 'bottom'}
          />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              aria-label='Switch organization'
              tooltip={currentOrg.name}
              className='!p-0 justify-center data-[state=open]:bg-sidebar-accent'
            >
              {orgMark}
              <span className='sr-only'>{currentOrg.name}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='min-w-64 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-xs text-muted-foreground'>
              Organizations
            </DropdownMenuLabel>
            {/* A switcher is a single choice, so the rows announce themselves
                as radios -- otherwise the current org reads as colour only. */}
            <DropdownMenuGroup>
              {orgs?.map((org) => {
                const isCurrentOrg = org.orgid === currentOrg.orgid;
                const showOrgLogoText = org.branding?.show_logo_text ?? true;
                return (
                  <DropdownMenuItem
                    key={org.orgid}
                    role='menuitemradio'
                    aria-checked={isCurrentOrg}
                    onClick={() => {
                      if (!isCurrentOrg) {
                        const remainingPath = stripOrgPrefix(
                          window.location.pathname,
                        );
                        window.location.href = `/o/${org.orgid}${remainingPath}${window.location.search}${window.location.hash}`;
                      }
                    }}
                    className={cn(
                      'gap-2 p-2',
                      isCurrentOrg &&
                        'bg-sidebar-accent text-sidebar-accent-foreground',
                    )}
                  >
                    <div
                      className={`flex items-center justify-start shrink-0 ${
                        !showOrgLogoText ? 'h-6 min-w-0 max-w-none' : 'size-6'
                      }`}
                    >
                      <OrgImage
                        imageUrl={org.branding?.logo_url}
                        className={`object-contain ${
                          !showOrgLogoText
                            ? 'h-6 w-auto max-w-none'
                            : 'size-6 rounded'
                        }`}
                      />
                    </div>
                    {showOrgLogoText && <span>{org.name}</span>}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className='gap-2 p-2'
                  onClick={() => setIsCreateOrgOpen(true)}
                >
                  <div className='flex size-6 shrink-0 items-center justify-center rounded bg-sidebar-accent'>
                    <Plus className='size-4' />
                  </div>
                  <span>Create organization</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className='flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground'>
                  <span className='truncate flex-1'>{currentOrg.orgid}</span>
                  <CopyButton
                    text={currentOrg.orgid}
                    size='small'
                    aria-label='Copy organization ID'
                  />
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <RegionBadge
          variant={state === 'collapsed' ? 'rail' : 'inline'}
          tooltipSide={state === 'collapsed' ? 'right' : 'bottom'}
        />
      </SidebarMenuItem>
      <CreateOrganizationModal
        isOpen={isCreateOrgOpen}
        setCreateOrgModalOpen={setIsCreateOrgOpen}
        updateOrganizationList={(created) =>
          setOrgs([...(orgs ?? []), { orgid: created.id, name: created.name }])
        }
      />
    </SidebarMenu>
  );
}
